import { Link } from '@inertiajs/react'
import { useState } from 'react'

const TXN_LABELS = {
  topup:           { label: 'Top-up',         color: 'text-green-700', bg: 'bg-green-100' },
  debit:           { label: 'Debit',           color: 'text-red-700',   bg: 'bg-red-100' },
  refund:          { label: 'Refund',          color: 'text-green-700', bg: 'bg-green-100' },
  escrow_hold:     { label: 'Escrow Hold',     color: 'text-yellow-700', bg: 'bg-yellow-100' },
  escrow_release:  { label: 'Escrow Release',  color: 'text-blue-700',  bg: 'bg-blue-100' }
}

export default function Show({ wallet_balance, transactions }) {
  const [amount, setAmount]     = useState('')
  const [source, setSource]     = useState('tok_visa')
  const [loading, setLoading]   = useState(false)

  const csrfToken = () =>
    document.querySelector('[name="csrf-token"]')?.content || ''

  const spendable = wallet_balance?.spendable_vuu_cents || 0
  const locked    = wallet_balance?.locked_vuu_cents    || 0

  const handleTopup = async (e) => {
    e.preventDefault()
    if (!amount || Number(amount) <= 0) return
    setLoading(true)
    const form = document.createElement('form')
    form.method = 'POST'
    form.action = '/wallet/topup'
    ;[
      ['amount_cents', Math.round(Number(amount) * 100)],
      ['card_source',  source],
      ['authenticity_token', csrfToken()]
    ].forEach(([name, value]) => {
      const i = document.createElement('input')
      i.type = 'hidden'; i.name = name; i.value = value
      form.appendChild(i)
    })
    document.body.appendChild(form)
    form.submit()
  }

  return (
    <div className="min-h-screen bg-gray-50 py-10 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Nav */}
        <div className="flex items-center justify-between mb-8">
          <button
            onClick={() => window.history.back()}
            className="text-blue-600 hover:text-blue-800 bg-transparent border-none cursor-pointer p-0"
          >
            ← Back
          </button>
          <Link href="/" className="text-gray-500 hover:text-gray-700 text-sm">Offers</Link>
        </div>

        <h1 className="text-3xl font-bold text-gray-900 mb-6">vuuCoin Wallet</h1>

        {/* Balance */}
        <div className="bg-white rounded-2xl shadow p-6 mb-6">
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-blue-50 rounded-xl p-4 text-center">
              <p className="text-sm text-gray-500 uppercase font-semibold mb-1">Spendable</p>
              <p className="text-3xl font-bold text-blue-700">{(spendable / 100).toFixed(2)}</p>
              <p className="text-xs text-gray-400 mt-1">vuu</p>
            </div>
            <div className="bg-yellow-50 rounded-xl p-4 text-center">
              <p className="text-sm text-gray-500 uppercase font-semibold mb-1">In Escrow</p>
              <p className="text-3xl font-bold text-yellow-700">{(locked / 100).toFixed(2)}</p>
              <p className="text-xs text-gray-400 mt-1">vuu</p>
            </div>
          </div>
        </div>

        {/* Top-up form */}
        <div className="bg-white rounded-2xl shadow p-6 mb-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4">Top Up (Mock / Test Mode)</h2>
          <form onSubmit={handleTopup} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Amount (USD = vuu, 1:1)
              </label>
              <input
                type="number"
                min="1"
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="10.00"
                className="w-full border border-gray-300 rounded-lg px-4 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-400"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Card Token (test mode)
              </label>
              <select
                value={source}
                onChange={(e) => setSource(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-4 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-400"
              >
                <option value="tok_visa">tok_visa (Visa — succeeds)</option>
                <option value="tok_mastercard">tok_mastercard (MC — succeeds)</option>
                <option value="tok_insufficient_funds">tok_insufficient_funds (declines)</option>
              </select>
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-4 rounded-lg transition-colors"
            >
              {loading ? 'Processing…' : 'Top Up Wallet'}
            </button>
          </form>
          <p className="text-xs text-gray-400 mt-3">
            🔒 Test mode: no real card charges. 1 USD = 1 vuu (1:1 rate).
          </p>
        </div>

        {/* Transaction history */}
        <div className="bg-white rounded-2xl shadow p-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4">Recent Transactions</h2>
          {transactions.length === 0 ? (
            <p className="text-gray-500 text-sm">No transactions yet.</p>
          ) : (
            <div className="divide-y divide-gray-100">
              {transactions.map((txn) => {
                const meta = TXN_LABELS[txn.txn_type] || { label: txn.txn_type, color: 'text-gray-700', bg: 'bg-gray-100' }
                const isCredit = ['topup', 'refund', 'escrow_release'].includes(txn.txn_type)
                return (
                  <div key={txn.id} className="py-3 flex items-center justify-between">
                    <div>
                      <span className={`inline-block text-xs font-semibold px-2 py-0.5 rounded-full ${meta.bg} ${meta.color} mr-2`}>
                        {meta.label}
                      </span>
                      <span className="text-xs text-gray-400">
                        {new Date(txn.created_at).toLocaleDateString()}
                      </span>
                    </div>
                    <span className={`font-bold ${isCredit ? 'text-green-700' : 'text-red-700'}`}>
                      {isCredit ? '+' : '-'}{(txn.amount_vuu_cents / 100).toFixed(2)} vuu
                    </span>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
